/**
 * Bugün on the phone (artboard F_Bugun; reordered after the panel audit of 25 Oct 2026, F2/F3): one inbox
 * number with "Sırayla incele", the four business tiles, then the decisions (Onaylar, Gönderilecek,
 * İstekler), and only after them readiness, the day's activity (collapsed), the team and the GM note.
 */

import { useState } from 'react';
import type { Proposal, Snapshot } from '../api/types.js';
import { usePanel } from '../app/context.js';
import { Link, navigate } from '../app/router.js';
import { counts, openRequests, orderedProposals, outboxWaiting, proposalGroups } from '../app/selectors.js';
import { Icon } from '../components/icons.js';
import { SectionHeader } from '../components/primitives.js';
import { ExpiredLine, PulseTiles } from '../components/pulse.js';
import { ApprovalRow, RequestRow, T4Band } from '../components/queue.js';
import { ReadinessSection } from '../components/readiness.js';
import { AppBar, BottomTabBar, StatusArea } from '../components/shell.js';
import { AgentMicroRow } from '../components/team.js';
import { TodaySection } from '../components/today.js';
import { formatClock, formatLongDate, formatUsdPair } from '../lib/format.js';
import { InboxHeadline, Loading } from './common.js';

/** "İlan başlığı · 4": the head of one type group in the queue (audit F4). */
export function QueueGroupHead({ label, count }: { label: string; count: number }) {
  return (
    <h3 className="queue-group">
      <span className="queue-group__label">{label}</span>
      <span className="mono queue-group__count">
        {count}
        <span className="sr-only"> kart</span>
      </span>
    </h3>
  );
}

export function ApprovalsSection({
  snapshot,
  now,
  headingId,
  review = true,
  grouped = false,
}: {
  snapshot: Snapshot;
  now: number;
  headingId: string;
  /** Bugün has the review button in its headline. */
  review?: boolean;
  /** Onaylar groups the queue by type with a count per group. */
  grouped?: boolean;
}) {
  const proposals = orderedProposals(snapshot);
  const t3 = proposals.filter((proposal) => proposal.tier === 'T3');
  // Anything that is not positively T3 sits under "Karar senin".
  const t4 = proposals.filter((proposal) => proposal.tier !== 'T3');
  const first = proposals[0];
  const rows = (items: readonly Proposal[]) =>
    grouped
      ? proposalGroups(items).map((group) => (
          <div key={`${group.tier}-${group.label}`} className="queue-group-block">
            <QueueGroupHead label={group.label} count={group.items.length} />
            {group.items.map((proposal) => (
              <ApprovalRow key={proposal.id} proposal={proposal} now={now} />
            ))}
          </div>
        ))
      : items.map((proposal) => <ApprovalRow key={proposal.id} proposal={proposal} now={now} />);
  return (
    <>
      {rows(t3)}
      {t4.length > 0 ? <T4Band /> : null}
      {rows(t4)}
      {first && review ? (
        <button
          type="button"
          className="review-btn"
          aria-describedby={headingId}
          onClick={() => navigate(`/onay/${first.id}`)}
        >
          Sırayla incele
          <span className="review-btn__sep" aria-hidden="true" />
          <span className="mono review-btn__count">{proposals.length} onay</span>
          <Icon name="chevron-right" size={18} />
        </button>
      ) : null}
    </>
  );
}

function GmNote({ snapshot }: { snapshot: Snapshot }) {
  const [open, setOpen] = useState(false);
  return (
    <section aria-labelledby="bugun-not" className="section">
      <SectionHeader
        id="bugun-not"
        title="GM sabah notu"
        right={
          <span className="section-header__aside">
            {snapshot.gmNote.model} · <span className="mono">{formatClock(snapshot.gmNote.at)}</span>
          </span>
        }
      />
      <button
        type="button"
        className={open ? 'gm-note gm-note--open' : 'gm-note'}
        aria-expanded={open}
        aria-label={open ? undefined : `GM sabah notu: ${snapshot.gmNote.lead} Notun tamamını aç`}
        onClick={() => setOpen(!open)}
      >
        {/* Floated before the text, so the second line wraps at a whole word before "Devamı". */}
        {open ? null : (
          <span className="gm-note__more" aria-hidden="true">
            … <span className="gm-note__more-label">Devamı</span>
          </span>
        )}
        <strong>{snapshot.gmNote.lead}</strong> {snapshot.gmNote.body}
        {open ? <span className="gm-note__source">{snapshot.gmNote.source}</span> : null}
      </button>
    </section>
  );
}

export function Bugun() {
  const state = usePanel();
  const snapshot = state.snapshot;
  if (!snapshot) {
    return (
      <>
        <AppBar title="Bugün" subtitle={formatLongDate(state.now)} logo />
        <Loading />
        <BottomTabBar active="bugun" />
      </>
    );
  }
  const now = state.now;
  const c = counts(snapshot);
  const requests = openRequests(snapshot);
  const toSend = outboxWaiting(snapshot);
  return (
    <>
      <AppBar title="Bugün" subtitle={formatLongDate(now)} logo />
      <StatusArea />
      <InboxHeadline snapshot={snapshot} now={now} review />
      <main className="phone-main">
        <PulseTiles snapshot={snapshot} />
        {c.approvals > 0 ? (
          <section aria-labelledby="bugun-onaylar" className="section">
            <SectionHeader
              id="bugun-onaylar"
              title="Onaylar"
              count={c.approvals}
              action={{ label: 'Tümü', to: '/onaylar' }}
            />
            <ApprovalsSection snapshot={snapshot} now={now} headingId="bugun-onaylar" review={false} />
          </section>
        ) : null}
        <ExpiredLine snapshot={snapshot} />

        {toSend > 0 ? (
          <Link
            to="/gonderilecekler"
            className="send-row"
            aria-label={`Gönderilecek: ${toSend} mesaj elle gönderilmeyi bekliyor. Gönderilecekler ekranını aç`}
          >
            <Icon name="send" size={18} />
            <span className="send-row__text">
              <span className="send-row__title">Gönderilecek</span>
              <span className="send-row__meta">E-posta cevabı, teslimat mesajı ya da yeni ilan; sen gönderirsin</span>
            </span>
            <span className="badge badge--solid">{toSend}</span>
            <Icon name="chevron-right" size={18} className="ink-3" />
          </Link>
        ) : null}

        {requests.length > 0 ? (
          <section aria-labelledby="bugun-istekler" className="section">
            <SectionHeader
              id="bugun-istekler"
              title="İstekler"
              count={requests.length}
              action={{ label: 'Tümü', to: '/istekler' }}
            />
            {requests.map((request) => (
              <RequestRow key={request.id} request={request} now={now} />
            ))}
          </section>
        ) : null}

        <ReadinessSection items={snapshot.readiness} now={now} />
        <TodaySection snapshot={snapshot} />

        <section aria-labelledby="bugun-ekip" className="section">
          <SectionHeader
            id="bugun-ekip"
            title="Ekip şimdi"
            right={
              <Link
                to="/ekip"
                className="section-header__budget"
                aria-label="Genesis bu ayki harcaması, Ekip ekranını aç"
              >
                <span>Genesis · {snapshot.store.monthLabel}</span>
                <span className="mono">{formatUsdPair(snapshot.genesis.spent, snapshot.genesis.cap)}</span>
                <Icon name="chevron-right" size={16} />
              </Link>
            }
          />
          {snapshot.agents.map((agent) => (
            <AgentMicroRow key={agent.id} agent={agent} />
          ))}
        </section>

        <GmNote snapshot={snapshot} />
      </main>
      <BottomTabBar active="bugun" />
    </>
  );
}
